import { useCallback, useMemo } from 'react';
import { CONTENT_ACTION_IDS } from 'content-management-actions-lib/constants/actionIds';
import { archive as archiveReconciliation, publish as publishReconciliation, unarchive as unarchiveReconciliation, unpublish as unpublishReconciliation, changeAudienceAccess as changeAudienceAccessReconciliation, assignPermissions as assignPermissionsReconciliation } from 'content-management-actions-lib/utils/reconciliationUtils';
import { useContentDetailsDataProvider } from '../providers/ContentDetailsDataProvider';
import { addToLanguageGroup as addToLanguageGroupReconciliation, convertToLanguageGroupPrimary as convertToLanguageGroupPrimaryReconciliation, createLanguageVariant as createLanguageVariantReconciliation, deleteContent as deleteContentReconciliation, removeFromMultiLanguageGroup, switchLanguageGroupPrimary as switchLanguageGroupPrimaryReconciliation } from '../utils/reconciliationUtils';
import useUpdateContentObject from './useUpdateContentObject';
const useActionSuccessUpdateHandlers = ({
  actionSuccessHandlerOverrides = {},
  allActions
}) => {
  const {
    contentObject
  } = useContentDetailsDataProvider();
  const {
    updateContentObject
  } = useUpdateContentObject();
  const archive = useCallback(() => {
    const updatedContentObjects = archiveReconciliation([contentObject]);
    updateContentObject(updatedContentObjects);
  }, [updateContentObject, contentObject]);
  const unarchive = useCallback(() => {
    const updatedContentObjects = unarchiveReconciliation([contentObject]);
    updateContentObject(updatedContentObjects);
  }, [updateContentObject, contentObject]);
  const publish = useCallback(({
    actionData
  }) => {
    const updatedContentObjects = publishReconciliation([contentObject], actionData.scheduledDate);
    updateContentObject(updatedContentObjects);
  }, [updateContentObject, contentObject]);
  const unpublish = useCallback(({
    actionData
  }) => {
    const updatedContentObjects = unpublishReconciliation([contentObject], actionData.scheduledDate);
    updateContentObject(updatedContentObjects);
  }, [updateContentObject, contentObject]);
  const deleteContent = useCallback(() => {
    const updatedContentObject = deleteContentReconciliation(contentObject);
    updateContentObject(updatedContentObject);
  }, [contentObject, updateContentObject]);
  const changeAudienceAccess = useCallback(({
    actionData,
    completedResults
  }) => {
    const updatedContentObjects = changeAudienceAccessReconciliation(completedResults, actionData);
    updateContentObject(updatedContentObjects);
  }, [updateContentObject]);
  const cloneContent = useCallback(({
    completedResults: [contentCrmObject]
  }) => {
    if (allActions.edit && allActions.edit.onActionButtonClick) {
      allActions.edit.onActionButtonClick(contentCrmObject);
    }
  }, [allActions.edit]);

  // Multi-language actions
  const removeFromLanguageGroup = useCallback(({
    completedResults: [contentCrmObject]
  }) => {
    const updatedContentCrmObject = removeFromMultiLanguageGroup(contentCrmObject);
    updateContentObject(updatedContentCrmObject);
  }, [updateContentObject]);
  const convertToLanguageGroupPrimary = useCallback(({
    completedResults: [contentCrmObject]
  }) => {
    const updatedContentCrmObject = convertToLanguageGroupPrimaryReconciliation(contentCrmObject);
    updateContentObject(updatedContentCrmObject);
  }, [updateContentObject]);
  const switchLanguageGroupPrimary = useCallback(({
    completedResults: [contentCrmObject],
    actionData
  }) => {
    const updatedContentCrmObject = switchLanguageGroupPrimaryReconciliation(contentCrmObject, actionData.newPrimaryId);
    updateContentObject(updatedContentCrmObject);
  }, [updateContentObject]);
  const addToLanguageGroup = useCallback(({
    completedResults: [contentCrmObject],
    actionData
  }) => {
    const {
      primaryId,
      selectedPrimaryContent
    } = actionData;
    const updatedContentObject = addToLanguageGroupReconciliation(contentCrmObject, primaryId, selectedPrimaryContent);
    updateContentObject(updatedContentObject);
  }, [updateContentObject]);
  const createLanguageVariant = useCallback(({
    completedResults: [contentCrmObject],
    actionData
  }) => {
    const updatedContentCrmObject = createLanguageVariantReconciliation(contentCrmObject, actionData);
    updateContentObject(updatedContentCrmObject);
  }, [updateContentObject]);
  const assignPermissions = useCallback(({
    actionData
  }) => {
    const {
      teamIds,
      userIds
    } = actionData;
    const updatedContentCrmObject = assignPermissionsReconciliation([contentObject], {
      teamIds,
      userIds
    });
    updateContentObject(updatedContentCrmObject);
  }, [contentObject, updateContentObject]);
  const actionSuccessUpdateHandlers = useMemo(() => Object.assign({
    [CONTENT_ACTION_IDS.ARCHIVE]: archive,
    [CONTENT_ACTION_IDS.UNARCHIVE]: unarchive,
    [CONTENT_ACTION_IDS.PUBLISHING_OPTIONS]: publish,
    [CONTENT_ACTION_IDS.UNPUBLISHING_OPTIONS]: unpublish,
    [CONTENT_ACTION_IDS.DELETE]: deleteContent,
    [CONTENT_ACTION_IDS.CONTROL_AUDIENCE_ACCESS]: changeAudienceAccess,
    [CONTENT_ACTION_IDS.CLONE]: cloneContent,
    [CONTENT_ACTION_IDS.ASSIGN_PERMISSIONS]: assignPermissions,
    // mLang actions
    [CONTENT_ACTION_IDS.REMOVE_FROM_LANGUAGE_GROUP]: removeFromLanguageGroup,
    [CONTENT_ACTION_IDS.CONVERT_TO_LANGUAGE_GROUP_PRIMARY]: convertToLanguageGroupPrimary,
    [CONTENT_ACTION_IDS.CREATE_LANGUAGE_VARIANT]: createLanguageVariant,
    [CONTENT_ACTION_IDS.ADD_TO_LANGUAGE_GROUP]: addToLanguageGroup,
    [CONTENT_ACTION_IDS.SWITCH_LANGUAGE_GROUP_PRIMARY]: switchLanguageGroupPrimary
  }, actionSuccessHandlerOverrides), [archive, unarchive, publish, unpublish, deleteContent, changeAudienceAccess, cloneContent, assignPermissions, removeFromLanguageGroup, convertToLanguageGroupPrimary, createLanguageVariant, addToLanguageGroup, switchLanguageGroupPrimary, actionSuccessHandlerOverrides]);
  return actionSuccessUpdateHandlers;
};
export default useActionSuccessUpdateHandlers;